/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core/styles';
import { Tab, Tabs, Paper } from '@material-ui/core';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Spinner from '@salesforce/design-system-react/components/spinner';
import Button from '@salesforce/design-system-react/components/button';
import ExpenseInventoryListHeader from './ExpenseInventoryListHeader';
import ExpenseInventoryDataTable, { ColumnSort } from './ExpenseInventoryDataTable';
import { GET_EXPENSE_INVENTORY_LIST } from '../../../../graphql/getExpenseInventoryList';
import { GET_FILTER_PRESETS } from '../../../../graphql/getFilterPresets';
import {
  LIMIT,
  NEXT_DATA,
  PREV_DATA,
  EXPENSE_INVENTORY_ROUTES,
  EXPENSE_INVENTORY_SCREEN_TYPE,
} from '../../../../util/constants';
import {
  statusFromPathname,
  tabIndexForStatus,
  ACTIVE_TYPE_LABEL_BY_STATUS,
} from '../../../../util/expenseInventory';
import { getUserId } from '../../../../store/ducks/userId';
import FilterTags from '../../../Shared/FilterTags';
import {
  clearExpenseInventoryFilters,
  getExpenseInventoryFilters,
  setExpenseInventoryFilters,
} from '../../../../store/ducks/expenseInventoryFilters';
import debounced from '../../../../util/debounced';
import '../../index.scss';

interface DynamicValue {
  [key: string]: any;
}
interface FilterPreset {
  filterValues: DynamicValue;
  filterExternalId?: string;
  filterName?: string;
}
type Props = RouteComponentProps;

const FILTER_PRESET_TYPE = 'ExpenseInventory';

const TAB_STYLE = {
  minHeight: '42px', // Overrides default 48px
  height: '42px',
  margin: '0 8px', // Optional: Adds some spacing between tabs
  fontSize: '16px', // Sets the base font size
  textTransform: 'none' as const, // Optional: Stops Material-UI from making text all-caps
  fontWeight: 500, // Regular weight for unselected tabs

  // 1. Force the layout engine to include borders inside the total height
  boxSizing: 'border-box' as const,

  // 2. Set a transparent bottom border on the base state so the tab's
  // internal content box size remains identical whether selected or not
  borderBottom: '5px solid transparent',

  // Target the built-in active state class name
  '&.Mui-selected': {
    fontWeight: 700,
    background: '#0000',
    borderBottom: '5px solid #ffb500',
    borderLeft: '1px solid #0000',
    borderRight: '1px solid #0000',
  },
};

const useStyles = makeStyles({
  tabsRoot: {
    minHeight: '42px',  // Overrides default 48px
    height: '42px',
  },
  tabRoot: {
    ...TAB_STYLE,
    minWidth: '94px',
  },
  tabRootWide: {
    ...TAB_STYLE,
    minWidth: '124px',
  },
});


const getFilterApiValue = (filterValues: DynamicValue): DynamicValue => {
  if (!filterValues) {
    return {};
  }
  return Object.keys(filterValues).reduce((reducedFilterValues: DynamicValue, key: string) => {
    if (Array.isArray(filterValues[key])) {
      const values = filterValues[key].map((item: any) => item.id);
      return values.length > 0 ? { ...reducedFilterValues, [key]: values } : reducedFilterValues;
    }
    if (key !== '__typename' && filterValues[key]) {
      return { ...reducedFilterValues, [key]: filterValues[key] };
    }
    return reducedFilterValues;
  }, {});
};

const ExpenseInventoryList: FC<Props> = ({ location, history }) => {
  const classes = useStyles();
  const userId = useSelector(getUserId);
  const dispatch = useDispatch();
  const [offset, setOffset] = useState(0);
  const [sortColumn, setSortColumn] = useState<ColumnSort>();
  const [status, setStatus] = useState(statusFromPathname(location.pathname));
  const [selectedFilter, setSelectedFilter] = useState<FilterPreset | DynamicValue | null>(null);
  const [updatedSelectedFilter, setUpdatedSelectedFilter] = useState<
    FilterPreset | DynamicValue | null
  >(null);
  const [selectedFilterApiValues, setSelectedFilterApiValues] = useState<DynamicValue | null>(
    null
  );
  const [isEditFilter, setIsEditFilter] = useState(false);

  const [
    getExpenseInventoryListItems,
    { data: expenseInventoryItems, loading, variables: queryVariables },
  ] = useLazyQuery(GET_EXPENSE_INVENTORY_LIST, { fetchPolicy: 'cache-and-network' });

  const { data: filterPresets } = useQuery(GET_FILTER_PRESETS, {
    variables: {
      userId,
      type: FILTER_PRESET_TYPE,
    },
  });
  const filterData = filterPresets?.getFilterPresets;

  // Switching tabs updates `status` one render before the lazy query below is
  // re-fired with that new status as its `type` variable (the fetch is kicked
  // off from a separate effect that reacts to the `status` change). Until that
  // fetch is in flight, the hook's `data`/`variables` still reflect the
  // previously selected tab. Gate the rendered rows on `queryVariables.type`
  // matching the currently selected tab so a still-settling previous-tab
  // result is never displayed under the newly selected tab.
  const isAwaitingTabQuery = queryVariables !== undefined && queryVariables.type !== status;
  const items = isAwaitingTabQuery ? [] : expenseInventoryItems?.getExpenseInventoryList || [];
  const isLoadingList = loading || isAwaitingTabQuery;

  const fetchList = useCallback(
    (nextOffset: number, filters: DynamicValue | null, sort: ColumnSort | undefined = sortColumn): void => {
      getExpenseInventoryListItems({
        variables: {
          offset: nextOffset,
          limit: LIMIT,
          type: status,
          orderBy: sort?.property,
          orderSortType: sort?.sortDirection,
          filters: filters && Object.keys(filters).length > 0 ? filters : null,
        },
      });
    },
    [status, sortColumn, getExpenseInventoryListItems]
  );

  const fetchFromStart = useCallback(
    (filters: DynamicValue | null): void => {
      setOffset(0);
      fetchList(0, filters);
    },
    [fetchList]
  );

  useEffect(() => {
    setStatus(statusFromPathname(location.pathname));
  }, [location.pathname]);

  const filterStore = useSelector(getExpenseInventoryFilters);
  useEffect(() => {
    let storedFilters;
    try {
      storedFilters = JSON.parse(filterStore || '{}');
    } catch {
      storedFilters = {};
    }
    let filtersToApply = selectedFilterApiValues;

    if (storedFilters?.filterValues) {
      setUpdatedSelectedFilter(storedFilters);
      setSelectedFilter(storedFilters);
      filtersToApply = getFilterApiValue(storedFilters.filterValues);
      setSelectedFilterApiValues(filtersToApply);
    }

    fetchFromStart(filtersToApply);
  }, [status, userId]);

  const handleTabChange = useCallback(
    (_event: React.ChangeEvent<{}>, newTabIndex: number): void => {
      if (newTabIndex === 1) {
        history.push(EXPENSE_INVENTORY_ROUTES.FAILED);
      } else if (newTabIndex === 2) {
        history.push(EXPENSE_INVENTORY_ROUTES.PROCESSED);
      } else {
        history.push(EXPENSE_INVENTORY_ROUTES.BASE);
      }
    },
    [history]
  );

  const setRequestSearch = useCallback(
    (searchedItem: string): void => {
      fetchFromStart({ ...selectedFilterApiValues, searchText: searchedItem });
    },
    [fetchFromStart, selectedFilterApiValues]
  );

  const debouncedFetchItems = useCallback(debounced(500, setRequestSearch), [status]);

  useEffect(() => {
    return (): void => {
      debouncedFetchItems.cancel();
    };
  }, [debouncedFetchItems]);

  const handleSearch = useCallback(
    (_event: { persist: () => void }, searchedItem: { value: string }) => {
      const { value } = searchedItem;
      _event.persist();
      debouncedFetchItems(value);
    },
    [debouncedFetchItems]
  );

  const handleRequestFilter = useCallback((): void => {
    setSelectedFilter(updatedSelectedFilter);
  }, [updatedSelectedFilter]);

  const resetFilters = useCallback((): void => {
    setSelectedFilter(null);
    setUpdatedSelectedFilter(null);
    setIsEditFilter(false);
    setSelectedFilterApiValues(null);
    fetchFromStart(null);
    dispatch(clearExpenseInventoryFilters());
  }, [fetchFromStart, dispatch]);

  const handleFilterEvents = useCallback(
    (filterValues: DynamicValue | null, filterName?: string): void => {
      const filter = { filterValues, filterName: filterName || '' };
      setUpdatedSelectedFilter(filter);
      setSelectedFilter(filter);
      if (filterValues) {
        const filters = getFilterApiValue(filterValues);
        setSelectedFilterApiValues(filters);
        fetchFromStart(filters);
        dispatch(setExpenseInventoryFilters(JSON.stringify(filter)));
      } else {
        resetFilters();
      }
    },
    [fetchFromStart, dispatch, resetFilters]
  );

  const handleEditPress = useCallback((filter: FilterPreset, isEditPress: boolean): void => {
    setSelectedFilter(filter);
    if (isEditPress) {
      setIsEditFilter(true);
    }
  }, []);

  const handlePaginationButtonClicked = useCallback(
    (type: string): void => {
      const finalOffset = type === NEXT_DATA ? offset + LIMIT : offset - LIMIT;
      setOffset(finalOffset);
      fetchList(finalOffset, selectedFilterApiValues);
    },
    [offset, fetchList, selectedFilterApiValues]
  );

  const handleSortedColumn = useCallback((column: ColumnSort): void => {
    setSortColumn(column);
  }, []);

  const activeType =
    ACTIVE_TYPE_LABEL_BY_STATUS[status] ||
    ACTIVE_TYPE_LABEL_BY_STATUS[EXPENSE_INVENTORY_SCREEN_TYPE.OPEN];

  const handleRefetch = useCallback((): void => {
    fetchList(offset, selectedFilterApiValues);
  }, [fetchList, offset, selectedFilterApiValues]);

  const handlePrevClick = useCallback((): void => handlePaginationButtonClicked(PREV_DATA), [
    handlePaginationButtonClicked,
  ]);

  const handleNextClick = useCallback((): void => handlePaginationButtonClicked(NEXT_DATA), [
    handlePaginationButtonClicked,
  ]);

  return (
    <IconSettings iconPath="/icons">
      <ExpenseInventoryListHeader
        handleSearch={handleSearch}
        handleFilter={handleRequestFilter}
        filterPresets={filterData}
        onEditPress={handleEditPress}
        handleFilterEvents={handleFilterEvents}
        resetFilters={resetFilters}
        type={status}
        selectedFilterApiValues={selectedFilterApiValues}
        refetch={handleRefetch}
      />
      <Paper className="inventory-tabs-scoped slds-tabs_scoped">
        <Tabs
          TabIndicatorProps={{ style: { display: 'none' } }}
          className="inventory-tabs-scoped slds-tabs_scoped__nav"
          classes={{ root: classes.tabsRoot }}
          value={tabIndexForStatus(status)}
          onChange={handleTabChange}
          aria-label="expense_inventory_tab_selection"
        >
          <Tab classes={{ root: classes.tabRoot }} label={ACTIVE_TYPE_LABEL_BY_STATUS[EXPENSE_INVENTORY_SCREEN_TYPE.OPEN]} disableRipple />
          <Tab classes={{ root: classes.tabRoot }} label={ACTIVE_TYPE_LABEL_BY_STATUS[EXPENSE_INVENTORY_SCREEN_TYPE.FAILED]} disableRipple />
          <Tab classes={{ root: classes.tabRootWide }} label={ACTIVE_TYPE_LABEL_BY_STATUS[EXPENSE_INVENTORY_SCREEN_TYPE.PROCESSED]} disableRipple />
        </Tabs>
      </Paper>
      {selectedFilter && selectedFilter.filterValues && !isEditFilter && (
        <FilterTags selectedFilter={selectedFilter} handleFilter={handleRequestFilter} />
      )}
      <div className="slds-tabs_scoped slds-tabs_scoped__nav">
        {isLoadingList && (
          <Spinner size="large" variant="base" assistiveText={{ label: 'Loading...' }} />
        )}
        {items?.length > 0 ? (
          <ExpenseInventoryDataTable
            orders={items}
            offsetData={offset}
            type={activeType}
            status={status}
            activeSort={sortColumn}
            onSortChange={handleSortedColumn}
            getRequestsList={getExpenseInventoryListItems}
            filters={selectedFilterApiValues}
          />
        ) : (
          !isLoadingList && (
            <div className="no-data-found-container">
              <h1 className="no-data-found-text">No Records Found</h1>
            </div>
          )
        )}
      </div>
      <div className="slds-grid slds-grid_align-center slds-grid_vertical-align-center slds-p-top_medium">
        <div className="slds-col">
          <Button
            assistiveText={{ icon: 'Icon Bare Small' }}
            iconCategory="utility"
            iconName="chevronleft"
            iconSize="small"
            disabled={!(offset > 0)}
            iconVariant="bare"
            onClick={handlePrevClick}
            variant="icon"
          />
        </div>
        <div className="slds-col slds-p-left_medium slds-p-right_medium">
          <span>{`${offset + 1} - ${offset + LIMIT}`}</span>
        </div>
        <div className="slds-col">
          <Button
            assistiveText={{ icon: 'Icon Bare Small' }}
            iconCategory="utility"
            iconName="chevronright"
            iconSize="small"
            iconVariant="bare"
            disabled={items && items.length < LIMIT}
            onClick={handleNextClick}
            variant="icon"
          />
        </div>
      </div>
    </IconSettings>
  );
};

export default withRouter(ExpenseInventoryList);
